import { useMemo } from 'react';
import {
  PERSONAL_MENU_ITEMS,
  PROJECT_MENU_ITEMS,
  TEAM_MENU_ITEMS,
  type MenuConfig,
  type MenuItem,
} from '~/components/dialogs/settings/SettingsDialog/constants';
import { SettingsPage } from '~/lib/stores/menu';
import { userStore } from '~/lib/stores/user';
import type { OrganizationData } from '~/types/organization';

type Params = {
  activeOrganization: OrganizationData | null;
};

export function useSettingsMenuConfigs({ activeOrganization }: Params): {
  projectSectionConfig: MenuConfig;
  personalSectionConfig: MenuConfig;
  teamSectionConfig: MenuConfig;
} {
  const isAdmin = activeOrganization ? activeOrganization.role === 'admin' : true;
  const featureFlags = userStore.featureFlags.get();

  const projectSectionConfig = useMemo<MenuConfig>(() => {
    const items = PROJECT_MENU_ITEMS.items
      .filter((item: MenuItem) => {
        if (
          // hide Edge Functions and Secretes screen for now if database flag is not enabled
          (item.id === SettingsPage.EdgeFunctions || item.id === SettingsPage.Secrets) &&
          !featureFlags.supabaseManagement
        ) {
          return false;
        }

        return true;
      })
      .map((item: MenuItem) => {
        if (
          (item.id === SettingsPage.DatabaseSection ||
            item.id === SettingsPage.Authentication ||
            item.id === SettingsPage.FileStorage ||
            item.id === SettingsPage.UserManagement) &&
          featureFlags.supabaseManagement
        ) {
          return { ...item, isComingSoon: false };
        }

        return item;
      });

    return {
      title: PROJECT_MENU_ITEMS.title,
      items,
    };
  }, [featureFlags.supabaseManagement]);

  const personalSectionConfig = useMemo<MenuConfig>(() => {
    const items = PERSONAL_MENU_ITEMS.items.filter((item: MenuItem) => {
      // show SubscriptionAndTokens and Cloud in Personal section only when there's no active organization
      if (item.id === SettingsPage.SubscriptionAndTokens || item.id === SettingsPage.Cloud) {
        return !activeOrganization;
      }

      return true;
    });

    return {
      title: PERSONAL_MENU_ITEMS.title,
      items,
    };
  }, [activeOrganization]);

  const teamSectionConfig = useMemo<MenuConfig>(() => {
    if (!activeOrganization) {
      return { title: '', items: [] };
    }

    const items = TEAM_MENU_ITEMS.items.filter(
      (item: MenuItem) => (isAdmin && featureFlags.privateRegistries) || !item.isTeamAdmin,
    );

    return {
      title: `${TEAM_MENU_ITEMS.title} ${activeOrganization.name}`,
      items,
    };
  }, [activeOrganization, isAdmin, featureFlags.privateRegistries]);

  return { projectSectionConfig, personalSectionConfig, teamSectionConfig };
}
